import { useMemo, useState } from 'react';
import { useCustom, useCustomMutation } from '@refinedev/core';
import {
  Loader2,
  Shield,
  MessageSquare,
  Award,
  BookOpen,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  CreditCard,
  ShoppingCart,
  UserPlus,
  HelpCircle,
  Edit3,
  Plus,
  Minus,
  ToggleLeft,
  Trash2,
  Radio,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/ui/date-picker';
import { endOfDayUtcIso, dateFromUtcDay } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DataTable, type BulkAction, type Column } from '@/components/tables';
import { notify } from '@/lib/notify';
import { resolveApiUrl } from '@/providers/api-url';

const API_URL = resolveApiUrl();

type EntitlementValue = string | number | boolean | string[] | { targetName?: string } | null;

interface EntitlementMeta {
  bonusId?: number;
  bonusTemplateId?: number | null;
  bonusType?: string;
  currentValue?: EntitlementValue;
  initialValue?: EntitlementValue;
  lastAction?: string | null;
  lastModifiedBy?: string | null;
  lastModifiedAt?: string | null;
  accessGrantId?: number;
  targetId?: number | null;
  sourceId?: number | null;
  sourceSubscriptionMemberId?: number | null;
  sourceChannelPassId?: number | null;
  orderId?: number | null;
  membershipId?: number;
  channelId?: number;
  /** Статус членства в Telegram-канале (для доступа типа CHANNEL) */
  joinStatus?: string;
  /** Реально ли пользователь вступил в канал (membership ACTIVE) */
  joined?: boolean;
}

interface Entitlement {
  id: string;
  category: 'access' | 'membership' | 'bonus';
  name: string;
  type: string;
  sourceType: string;
  status: string;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string;
  meta: EntitlementMeta;
}

interface UserEntitlementsProps {
  userId: number;
}

const formatDateTime = (dateString: string) =>
  new Date(dateString).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const getCategoryLabel = (category: string) => {
  switch (category) {
    case 'access':
      return 'Доступ';
    case 'membership':
      return 'Членство';
    case 'bonus':
      return 'Привилегия';
    default:
      return category;
  }
};

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'access':
      return <Shield className="h-4 w-4" />;
    case 'membership':
      return <MessageSquare className="h-4 w-4" />;
    case 'bonus':
      return <Award className="h-4 w-4" />;
    default:
      return <HelpCircle className="h-4 w-4" />;
  }
};

const getTypeLabel = (type: string) => {
  const labels: Record<string, string> = {
    COURSE: 'Курс',
    CHANNEL: 'Канал',
    EVENT: 'Мероприятие',
    CONTENT: 'Контент',
    MODULE: 'Модуль',
    MODULE_ITEM: 'Урок',
    CHANNEL_PASS: 'Канал',
    SUBSCRIPTION_PLAN: 'Абонемент',
    BOOK: 'Книга',
    LIVESTREAM: 'Трансляция',
    NUMBER: 'Числовая',
    BOOLEAN: 'Переключатель',
    ARRAY: 'Список',
    STRING: 'Строковая',
  };
  return labels[type] ?? type;
};

const getTypeIcon = (type: string) => {
  switch (type) {
    case 'COURSE':
      return <BookOpen className="h-3.5 w-3.5" />;
    case 'CHANNEL':
    case 'CHANNEL_PASS':
      return <MessageSquare className="h-3.5 w-3.5" />;
    case 'LIVESTREAM':
      return <Radio className="h-3.5 w-3.5" />;
    default:
      return null;
  }
};

const getSourceLabel = (sourceType: string) => {
  const labels: Record<string, string> = {
    SUBSCRIPTION: 'Подписка',
    MANUAL: 'Вручную',
    PROMOTION: 'Акция',
    GIFT: 'Подарок',
    ORDER: 'Заказ',
    ADMIN: 'Админ',
    UNKNOWN: '—',
  };
  return labels[sourceType] ?? sourceType;
};

const getSourceIcon = (sourceType: string) => {
  switch (sourceType) {
    case 'SUBSCRIPTION':
      return <CreditCard className="h-3.5 w-3.5" />;
    case 'ORDER':
      return <ShoppingCart className="h-3.5 w-3.5" />;
    case 'MANUAL':
    case 'ADMIN':
      return <UserPlus className="h-3.5 w-3.5" />;
    default:
      return null;
  }
};

const getStatusBadge = (status: string, endsAt: string | null) => {
  const isExpired = endsAt && new Date(endsAt) < new Date();

  if (status === 'REVOKED' || status === 'KICKED') {
    return (
      <Badge variant="destructive" className="gap-1">
        <XCircle className="h-3 w-3" />
        {status === 'REVOKED' ? 'Отозван' : 'Исключён'}
      </Badge>
    );
  }

  if (status === 'EXPIRED' || isExpired) {
    return (
      <Badge variant="secondary" className="gap-1">
        <AlertCircle className="h-3 w-3" />
        Истёк
      </Badge>
    );
  }

  if (status === 'ACTIVE') {
    return (
      <Badge className="bg-green-500 gap-1">
        <CheckCircle2 className="h-3 w-3" />
        Активен
      </Badge>
    );
  }

  if (status === 'PENDING' || status === 'JOIN_REQUEST_SENT') {
    return (
      <Badge variant="outline" className="gap-1">
        <Clock className="h-3 w-3" />
        {status === 'PENDING' ? 'Ожидание' : 'Запрос отправлен'}
      </Badge>
    );
  }

  if (status === 'LEFT') {
    return <Badge variant="secondary">Вышел</Badge>;
  }

  if (status === 'BOT_BLOCKED') {
    return <Badge variant="destructive">Бот заблокирован</Badge>;
  }

  return <Badge variant="outline">{status}</Badge>;
};

// Под-метка вступления в Telegram-канал для строки доступа к каналу.
// joined=true → реально в канале; иначе — приглашён, но ещё не зашёл (или вышел/исключён).
const getJoinStateBadge = (joinStatus: string | undefined, joined: boolean | undefined) => {
  if (joinStatus === undefined) {
    return null;
  }

  if (joined) {
    return (
      <div className="mt-1 flex items-center gap-1 text-xs text-green-600 dark:text-green-500">
        <CheckCircle2 className="h-3 w-3" />
        В канале
      </div>
    );
  }

  if (joinStatus === 'LEFT') {
    return (
      <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
        <XCircle className="h-3 w-3" />
        Вышел из канала
      </div>
    );
  }

  if (joinStatus === 'KICKED') {
    return (
      <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
        <XCircle className="h-3 w-3" />
        Исключён из канала
      </div>
    );
  }

  // PENDING / JOIN_REQUEST_SENT / прочее — доступ есть, но человек ещё не вступил
  return (
    <div className="mt-1 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-500">
      <Clock className="h-3 w-3" />
      Не в канале
    </div>
  );
};

const getDaysRemaining = (endsAt: string | null): string | null => {
  if (!endsAt) {
    return null;
  }
  const diff = new Date(endsAt).getTime() - Date.now();
  if (diff <= 0) {
    return 'Истёк';
  }
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
  return `${days} дн.`;
};

const renderBonusValue = (value: EntitlementValue | undefined, type: string): string => {
  if (value === undefined || value === null) {
    return '(пусто)';
  }

  switch (type) {
    case 'NUMBER':
      return String(value);
    case 'BOOLEAN':
      return value ? 'Да' : 'Нет';
    case 'ARRAY':
      return Array.isArray(value) ? (value.length > 0 ? value.join(', ') : '(пусто)') : String(value);
    case 'STRING': {
      if (!Array.isArray(value) && typeof value === 'object' && typeof value.targetName === 'string') {
        return value.targetName;
      }
      return String(value) || '(пусто)';
    }
    default:
      return String(value);
  }
};

const getSourceDetails = (entitlement: Entitlement) => {
  const details = [
    entitlement.meta.orderId ? `заказ #${entitlement.meta.orderId}` : null,
    entitlement.meta.sourceId ? `источник #${entitlement.meta.sourceId}` : null,
    entitlement.meta.sourceSubscriptionMemberId ? `участник подписки #${entitlement.meta.sourceSubscriptionMemberId}` : null,
    entitlement.meta.sourceChannelPassId ? `пропуск #${entitlement.meta.sourceChannelPassId}` : null,
    entitlement.meta.accessGrantId ? `доступ #${entitlement.meta.accessGrantId}` : null,
    entitlement.meta.bonusId ? `привилегия #${entitlement.meta.bonusId}` : null,
    entitlement.meta.membershipId ? `членство #${entitlement.meta.membershipId}` : null,
  ].filter((detail): detail is string => detail !== null);

  return details.join(' · ');
};

const getActionLabel = (action: string): string => {
  const labels: Record<string, string> = {
    CREATE: 'Создание',
    INCREMENT: 'Увеличение',
    DECREMENT: 'Уменьшение',
    SET: 'Установка',
    TOGGLE: 'Переключение',
    PUSH: 'Добавление',
    REMOVE: 'Удаление',
    EXPIRE: 'Истечение',
    REVOKE: 'Отзыв',
  };
  return labels[action] ?? action;
};

export function UserEntitlements({ userId }: UserEntitlementsProps) {
  const entitlementsQuery = useCustom<Entitlement[]>({
    url: `/users/${userId}/entitlements`,
    method: 'get',
  });

  const isLoading = entitlementsQuery.query?.isLoading ?? false;
  const refetch = entitlementsQuery.query?.refetch;
  const entitlements: Entitlement[] = useMemo(
    () => entitlementsQuery.query?.data?.data ?? [],
    [entitlementsQuery.query?.data?.data],
  );

  // Edit bonus value dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingBonus, setEditingBonus] = useState<Entitlement | null>(null);
  const [editValue, setEditValue] = useState('');
  const actionMutation = useCustomMutation();

  // Revoke confirmation dialog
  const [revokeItems, setRevokeItems] = useState<Entitlement[]>([]);
  const [isRevoking, setIsRevoking] = useState(false);
  const revokeMutation = useCustomMutation();

  // Edit access endsAt dialog
  const [editAccess, setEditAccess] = useState<Entitlement | null>(null);
  const [editEndsAt, setEditEndsAt] = useState<Date | null>(null);
  const updateMutation = useCustomMutation();

  const handleEditAccessOpen = (entitlement: Entitlement) => {
    setEditAccess(entitlement);
    setEditEndsAt(entitlement.endsAt ? dateFromUtcDay(entitlement.endsAt) : null);
  };

  const handleSaveEndsAt = () => {
    const grantId = editAccess?.meta.accessGrantId;
    if (!grantId) {
      return;
    }
    updateMutation.mutate(
      {
        url: `${API_URL}/users/${userId}/access-grants/${grantId}`,
        method: 'patch',
        values: { endsAt: editEndsAt ? endOfDayUtcIso(editEndsAt) : null },
      },
      {
        onSuccess: () => {
          setEditAccess(null);
          refetch?.();
          notify.success('Срок действия обновлён');
        },
        onError: () => {
          notify.error('Ошибка при обновлении срока');
        },
      },
    );
  };

  const revokeEntitlement = (entitlement: Entitlement): Promise<boolean> =>
    new Promise((resolve) => {
      const onDone = (ok: boolean) => resolve(ok);

      if (entitlement.category === 'access') {
        const grantId = entitlement.meta.accessGrantId;
        if (!grantId) {
          onDone(false);
          return;
        }
        revokeMutation.mutate(
          {
            url: `${API_URL}/users/${userId}/access-grants/${grantId}`,
            method: 'delete',
            values: {},
          },
          {
            onSuccess: () => onDone(true),
            onError: () => onDone(false),
          },
        );
        return;
      }

      if (entitlement.category === 'bonus') {
        const bonusId = entitlement.meta.bonusId;
        if (!bonusId) {
          onDone(false);
          return;
        }
        revokeMutation.mutate(
          {
            url: `${API_URL}/user-bonuses/admin/${bonusId}/revoke`,
            method: 'patch',
            values: { reason: 'ADMIN_MANUAL_REVOKE' },
          },
          {
            onSuccess: () => onDone(true),
            onError: () => onDone(false),
          },
        );
        return;
      }

      if (entitlement.category === 'membership') {
        const membershipId = entitlement.meta.membershipId;
        if (!membershipId) {
          onDone(false);
          return;
        }
        revokeMutation.mutate(
          {
            url: `${API_URL}/channel-memberships/${membershipId}/status`,
            method: 'patch',
            values: { status: 'KICKED', reason: 'ADMIN_MANUAL_KICK' },
          },
          {
            onSuccess: () => onDone(true),
            onError: () => onDone(false),
          },
        );
        return;
      }

      onDone(false);
    });

  const handleRevokeConfirm = async () => {
    if (revokeItems.length === 0) {
      return;
    }

    setIsRevoking(true);
    const results = await Promise.all(revokeItems.map((item) => revokeEntitlement(item)));
    setIsRevoking(false);

    const successful = results.filter(Boolean).length;
    const failed = results.length - successful;

    setRevokeItems([]);
    refetch?.();

    if (failed === 0) {
      notify.success(
        successful === 1 ? 'Доступ отозван' : `Отозвано: ${successful}`,
      );
    } else if (successful === 0) {
      notify.error(
        revokeItems.length === 1
          ? 'Не удалось отозвать доступ'
          : 'Не удалось отозвать доступы',
      );
    } else {
      notify.warning(`Отозвано: ${successful}, ошибок: ${failed}`);
    }
  };

  const handleEditOpen = (entitlement: Entitlement) => {
    setEditingBonus(entitlement);
    setEditValue(String(entitlement.meta.currentValue ?? ''));
    setEditDialogOpen(true);
  };

  const handleQuickAction = (entitlement: Entitlement, action: string) => {
    const bonusId = entitlement.meta.bonusId;
    if (!bonusId) {
      return;
    }
    // Для increment/decrement value не нужно, но schema требует — передаём 1
    const needsValue = action === 'increment' || action === 'decrement';
    actionMutation.mutate(
      {
        url: `${API_URL}/user-bonuses/admin/${bonusId}`,
        method: 'patch',
        values: {
          action,
          ...(needsValue ? { value: 1 } : { value: true }),
          idempotencyKey: crypto.randomUUID(),
        },
      },
      {
        onSuccess: () => {
          refetch?.();
          notify.success('Действие выполнено');
        },
        onError: () => {
          notify.error('Ошибка при выполнении действия');
        },
      },
    );
  };

  const handleSetValue = () => {
    if (!editingBonus?.meta.bonusId) {
      return;
    }
    const bonusType = editingBonus.meta.bonusType;
    const parsedValue = bonusType === 'NUMBER' ? Number(editValue) : editValue;

    actionMutation.mutate(
      {
        url: `${API_URL}/user-bonuses/admin/${editingBonus.meta.bonusId}`,
        method: 'patch',
        values: {
          action: 'SET',
          value: parsedValue,
          idempotencyKey: crypto.randomUUID(),
        },
      },
      {
        onSuccess: () => {
          setEditDialogOpen(false);
          refetch?.();
          notify.success('Значение обновлено');
        },
        onError: () => {
          notify.error('Ошибка при обновлении значения');
        },
      },
    );
  };

  const columns: Column<Entitlement>[] = [
    {
      key: 'category',
      header: 'Категория',
      render: (_, record) => (
        <div className="flex items-center gap-2">
          {getCategoryIcon(record.category)}
          <span className="text-sm">{getCategoryLabel(record.category)}</span>
        </div>
      ),
    },
    {
      key: 'name',
      header: 'Название',
      render: (_, record) => (
        <div>
          <div className="font-medium">{record.name}</div>
          {record.category === 'bonus' && record.meta.currentValue !== undefined && (
            <div className="mt-0.5 text-xs text-muted-foreground">
              <span className="font-mono">
                {renderBonusValue(record.meta.currentValue, record.meta.bonusType ?? record.type)}
              </span>
              {record.meta.initialValue !== undefined &&
                JSON.stringify(record.meta.currentValue) !== JSON.stringify(record.meta.initialValue) && (
                  <span className="ml-1 opacity-60">
                    (изн. {renderBonusValue(record.meta.initialValue, record.meta.bonusType ?? record.type)})
                  </span>
                )}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Тип',
      render: (_, record) => (
        <div className="flex items-center gap-1.5">
          {getTypeIcon(record.type)}
          <span className="text-sm">{getTypeLabel(record.type)}</span>
        </div>
      ),
    },
    {
      key: 'sourceType',
      header: 'Источник',
      render: (_, record) => {
        const sourceDetails = getSourceDetails(record);
        return (
          <div>
            <div className="flex items-center gap-1.5 text-muted-foreground">
              {getSourceIcon(record.sourceType)}
              <span className="text-xs">{getSourceLabel(record.sourceType)}</span>
            </div>
            {record.category === 'bonus' && record.meta.lastModifiedBy && (
              <div className="mt-0.5 text-xs text-muted-foreground opacity-70">
                {record.meta.lastAction ? getActionLabel(record.meta.lastAction) : 'Изменено'}
                {' · '}
                {record.meta.lastModifiedBy}
              </div>
            )}
            {sourceDetails && (
              <div className="mt-0.5 text-xs text-muted-foreground opacity-70">
                {sourceDetails}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Статус',
      render: (_, record) => (
        <div>
          {getStatusBadge(record.status, record.endsAt)}
          {record.category === 'access' &&
            record.type === 'CHANNEL' &&
            getJoinStateBadge(record.meta.joinStatus, record.meta.joined)}
        </div>
      ),
    },
    {
      key: 'endsAt',
      header: 'Срок действия',
      render: (_, record) => {
        if (!record.endsAt) {
          return <span className="text-sm text-muted-foreground">Бессрочно</span>;
        }
        const remaining = getDaysRemaining(record.endsAt);
        return (
          <div className="text-sm">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Clock className="h-3 w-3" />
              до {formatDateTime(record.endsAt)}
            </div>
            {remaining && remaining !== 'Истёк' && (
              <span className="text-xs text-muted-foreground">({remaining})</span>
            )}
          </div>
        );
      },
    },
    {
      key: 'createdAt',
      header: 'Выдан',
      render: (value) => (
        <span className="text-sm text-muted-foreground">{formatDateTime(String(value))}</span>
      ),
    },
    {
      key: 'id',
      header: '',
      render: (_, record) => {
        const canEditEndsAt = record.category === 'access' && !!record.meta.accessGrantId;
        // Истёкший/отозванный доступ можно реактивировать правкой срока — для него
        // показываем только карандаш. Прочие неактивные записи — без действий.
        if (record.status !== 'ACTIVE') {
          if (!canEditEndsAt) {
            return null;
          }
          return (
            <div className="flex items-center justify-end gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => { e.stopPropagation(); handleEditAccessOpen(record); }}
                title="Изменить срок действия"
              >
                <Clock className="h-3 w-3" />
              </Button>
            </div>
          );
        }
        const bonusType = record.meta.bonusType ?? record.type;
        const isPending = actionMutation.mutation.isPending;
        const canRevoke =
          (record.category === 'access' && !!record.meta.accessGrantId) ||
          (record.category === 'bonus' && !!record.meta.bonusId) ||
          (record.category === 'membership' && !!record.meta.membershipId);
        return (
          <div className="flex items-center justify-end gap-1">
            {canEditEndsAt && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => { e.stopPropagation(); handleEditAccessOpen(record); }}
                title="Изменить срок действия"
              >
                <Clock className="h-3 w-3" />
              </Button>
            )}
            {record.category === 'bonus' && bonusType === 'NUMBER' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); handleQuickAction(record, 'increment'); }}
                  disabled={isPending}
                  title="Увеличить на 1"
                >
                  <Plus className="h-3 w-3" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); handleQuickAction(record, 'decrement'); }}
                  disabled={isPending}
                  title="Уменьшить на 1"
                >
                  <Minus className="h-3 w-3" />
                </Button>
              </>
            )}
            {record.category === 'bonus' && bonusType === 'BOOLEAN' && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => { e.stopPropagation(); handleQuickAction(record, 'TOGGLE'); }}
                disabled={isPending}
                title="Переключить"
              >
                <ToggleLeft className="h-3 w-3" />
              </Button>
            )}
            {record.category === 'bonus' && (bonusType === 'NUMBER' || bonusType === 'STRING') && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => { e.stopPropagation(); handleEditOpen(record); }}
                disabled={isPending}
                title="Изменить значение"
              >
                <Edit3 className="h-3 w-3" />
              </Button>
            )}
            {canRevoke && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => { e.stopPropagation(); setRevokeItems([record]); }}
                disabled={isPending}
                title="Отозвать"
                className="text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const bulkActions: BulkAction<Entitlement>[] = [
    {
      label: 'Отозвать',
      icon: <Trash2 className="h-4 w-4" />,
      variant: 'destructive',
      showOnlyWhenSelected: true,
      onClick: (items) => {
        const revokable = items.filter(
          (item) =>
            item.status === 'ACTIVE' &&
            ((item.category === 'access' && !!item.meta.accessGrantId) ||
              (item.category === 'bonus' && !!item.meta.bonusId) ||
              (item.category === 'membership' && !!item.meta.membershipId)),
        );
        if (revokable.length === 0) {
          notify.error('Нет записей, которые можно отозвать');
          return;
        }
        setRevokeItems(revokable);
      },
    },
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (entitlements.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        <Shield className="mx-auto mb-2 h-12 w-12 opacity-50" />
        <p>Нет доступов, членств и привилегий</p>
      </div>
    );
  }

  return (
    <>
      <DataTable
        columns={columns}
        data={entitlements}
        total={entitlements.length}
        page={1}
        pageSize={100}
        onPageChange={() => {}}
        emptyMessage="Нет доступов"
        selectable
        bulkActions={bulkActions}
      />

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Изменить значение</DialogTitle>
            <DialogDescription>Привилегия: {editingBonus?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Новое значение</Label>
              <Input
                type={editingBonus?.meta.bonusType === 'NUMBER' ? 'number' : 'text'}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={handleSetValue} disabled={actionMutation.mutation.isPending}>
              {actionMutation.mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editAccess !== null} onOpenChange={(open) => !open && setEditAccess(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Изменить срок действия</DialogTitle>
            <DialogDescription>{editAccess ? `Доступ: ${editAccess.name}` : ''}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Действует до</Label>
              <DatePicker
                value={editEndsAt}
                onChange={setEditEndsAt}
                placeholder="Бессрочный доступ"
              />
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  Оставьте пустым, чтобы сделать доступ бессрочным.
                </p>
                {editEndsAt && (
                  <Button variant="ghost" size="sm" onClick={() => setEditEndsAt(null)}>
                    Сделать бессрочным
                  </Button>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAccess(null)}>
              Отмена
            </Button>
            <Button onClick={handleSaveEndsAt} disabled={updateMutation.mutation.isPending}>
              {updateMutation.mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={revokeItems.length > 0}
        onOpenChange={(open) => {
          if (!open && !isRevoking) {
            setRevokeItems([]);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {revokeItems.length === 1
                ? 'Отозвать доступ?'
                : `Отозвать доступы (${revokeItems.length})?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {revokeItems.length === 1
                ? `Вы уверены, что хотите отозвать «${revokeItems[0]?.name}»? Это действие нельзя отменить.`
                : 'Выбранные доступы, членства и привилегии будут отозваны. Это действие нельзя отменить.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRevoking}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleRevokeConfirm();
              }}
              disabled={isRevoking}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRevoking && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Отозвать
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
